// Hand-rolled canvas meteogram renderer. Zero dependencies (DOM lib only).
// Time on X (hourly labels), a numeric quantity on Y. Line / area / points.
// Retina-crisp, redraws only on data / resize / cursor.
//
// The highlighted sample is not this chart's own hover state — it is derived from
// the SHARED timeline cursor (time/cursor.ts), so every stacked chart and the map
// field all highlight the same instant. Pointing at one chart moves them all.

import { getCursor, indexAtTime, subscribeCursor, timesOf } from "../time/cursor.js";
import { getWindowHours, subscribeWindow } from "../time/window.js";
import { fmtTickLabel, subscribeTimeMode } from "../ui/time.js";
import { buildReadout, readoutIndex, type ReadoutModel } from "./readout.js";

export interface Series {
  label: string;
  data: (number | null)[]; // aligned 1:1 with config.xLabels; null = gap (spanGaps: connect across)
  color: string; // CSS color
  width?: number; // line width px, default 2
  dashed?: boolean; // dashed line
  points?: boolean; // draw dots at each value (for observations); no connecting line unless `line` also set
  line?: boolean; // force a connecting line even when points=true
  fill?: "-1" | "origin" | false; // "-1" = fill to PREVIOUS series in the array; "origin" = fill to y=0; default false
  fillColor?: string; // fill color (usually a translucent rgba); defaults to color
  order?: number; // draw order, lower = earlier/behind; default array order
  noLegend?: boolean; // exclude from legend
  /** Exclude from the readout (chart/readout.ts).
   *
   *  Set it WITH noLegend or not at all. On a chart carrying `legend: false` the
   *  readout is the only thing naming the lines, so a series hidden from the
   *  readout but not the legend would be named by nothing at all — colour alone,
   *  which docs/CONVENTIONS.md rules out. Today the only users are the
   *  transparent envelope edges, which carry both. */
  noTooltip?: boolean;
}

export interface ChartCtx {
  ctx: CanvasRenderingContext2D;
  xPx(index: number): number; // data index -> device-independent pixel x (ctx already DPR-scaled)
  yPx(value: number): number; // data value -> pixel y
  area: { left: number; right: number; top: number; bottom: number };
  width: number;
  height: number; // CSS pixels of the plot area
  series: Series[];
  xLabels: string[];
}

export interface Mark {
  layer?: "back" | "front"; // back = behind series (default), front = above series
  draw(c: ChartCtx): void;
}

export interface ChartConfig {
  series: Series[];
  xLabels: string[]; // one per data point (e.g. "2026-07-11T14:00"); used for ticks + tooltip
  yUnit?: string; // appended to y tick labels and tooltip values, e.g. "kn"
  yMin?: number; // default 0
  yMax?: number; // default: computed from data with a "nice" ceiling
  marks?: Mark[];
  majorTick?: (label: string, index: number) => string | null; // string => draw a major x tick+label at index, null => skip
  tooltipTitle?: (index: number) => string; // header of the hover tooltip; default: the raw xLabel
  /** Extra muted footer lines under the series rows (e.g. squall reasoning). */
  tooltipExtra?: (index: number) => string[];
  /** Force the left margin in CSS px instead of measuring the widest y label.
   *
   *  Stacked tracks MUST share one value. Left to itself each chart sizes its own
   *  gutter from its tick text, so "10 m", "1200" and "0.5 kn" produce three
   *  different plot origins — and the shared playhead then lands at a different
   *  screen x on every track, which destroys the illusion that they are one
   *  timeline. */
  gutterLeft?: number;
  /** Draw the x tick labels along the bottom. Off for stacked tracks, which share
   *  a single ruler rather than repeating the date strip once per track. */
  xAxis?: boolean;
  /** Draw the swatch+label row inside the canvas. Default on.
   *
   *  Off wherever a `.creadout` strip sits above the plot (chart/readout.ts).
   *  That strip prints every tooltip series as swatch + name + VALUE, which is
   *  the legend's pairs plus the number, one line higher — so the canvas row
   *  restates the names and charges the plot 20 px for the restatement. On a
   *  76 px track that is 20 of 76; on a 118 px tall track, 20 of 118.
   *
   *  Left ON where there is no readout and no cursor to drive one: the printable
   *  report (which sets minDpr) and the forecast-vs-buoy pair. Those would
   *  otherwise be identifying their model lines by colour alone, which
   *  docs/CONVENTIONS.md forbids. The rule is satisfied either way — by the
   *  legend there, by the readout here — but it must be satisfied by ONE of
   *  them, so this flag is set per chart rather than inferred from the stack. */
  legend?: boolean;
  /** Floor for the backing-store scale. Screens use devicePixelRatio as ever;
   *  the printable report sets 2 so a chart rendered on a 1x display still
   *  rasterises at ~192 dpi when the browser prints it. */
  minDpr?: number;
}

export interface Chart {
  update(config: Partial<ChartConfig>): void; // merge + redraw
  destroy(): void; // remove listeners/observers
  /** The values at the described sample, published on every repaint. The text
   *  that used to be a box on the canvas is a DOM readout beside it now (see
   *  chart/readout.ts); this is how it is fed. Returns the unsubscribe. */
  subscribeReadout(fn: (m: ReadoutModel) => void): () => void;
}

// ---------------------------------------------------------------------------
// Helpers (pure)

// Round a positive magnitude up to the next "nice" number (1/2/5 x 10^n).
function niceCeil(v: number): number {
  if (!(v > 0)) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const f = v / base; // in [1, 10)
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * base;
}

// A "nice" step size (1/2/5 x 10^n) near the requested raw step.
function niceStep(raw: number): number {
  if (!(raw > 0)) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const f = raw / base;
  const nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  return nf * base;
}

function decimalsFor(step: number): number {
  if (step >= 1) return Number.isInteger(step) ? 0 : 1;
  return Math.min(4, Math.ceil(-Math.log10(step)));
}

// Major ticks come from the shared clock module so the date strip moves to UTC
// midnights when the console's clock is toggled — a tick can't stay on the
// site's midnight while the tooltip above it reads UTC.
function defaultMajorTick(label: string): string | null {
  return fmtTickLabel(label);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

interface Palette {
  grid: string;
  axis: string;
  muted: string;
  ink: string;
  ink2: string;
  surface: string;
  /** The shared playhead. --accent is the codebase's "interactive" colour, which
   *  is what a cursor is — and it keeps the playhead distinct from --now and
   *  --crit, both red, so the timeline position is never mistaken for an alarm. */
  cursor: string;
}

// Resolved from CSS custom properties, memoised per theme. The console has three
// ECDIS tables (Day/Dusk/Night) that swap by data-theme, so this cannot be read
// once at construction — but re-reading ~7 properties on every frame of playback
// would be wasteful, hence the key.
let paletteCache: { key: string; pal: Palette } | null = null;

function palette(): Palette {
  const key = document.documentElement.getAttribute("data-theme") ?? "day";
  if (paletteCache !== null && paletteCache.key === key) return paletteCache.pal;
  const pal = resolvePalette();
  paletteCache = { key, pal };
  return pal;
}

function resolvePalette(): Palette {
  const cs = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string => {
    const v = cs.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  return {
    grid: read("--grid", "rgba(128,128,128,0.16)"),
    axis: read("--axis", "rgba(128,128,128,0.42)"),
    muted: read("--muted", "#898781"),
    ink: read("--ink", "#0b0b0b"),
    ink2: read("--ink-2", "#52514e"),
    surface: read("--surface", "#ffffff"),
    cursor: read("--accent", "#0f6b74"),
  };
}

interface YTick {
  value: number;
  label: string;
}

/** Height the in-canvas legend row occupies, CSS px. */
const LEGEND_H = 20;

/**
 * The plot area's vertical margins, in CSS px.
 *
 * Pure and exported so what a row costs is a stated number rather than a claim:
 * the whole case for `legend: false` on a track is arithmetic against a 76 px
 * canvas, and arithmetic in the middle of a draw() is arithmetic nobody can
 * check. See test/chartmargins.test.ts.
 */
export function plotMargins(o: { legend: boolean; xAxis: boolean }): {
  top: number;
  bottom: number;
} {
  return { top: (o.legend ? LEGEND_H : 0) + 8, bottom: o.xAxis ? 22 : 6 };
}

// ---------------------------------------------------------------------------

export function createChart(canvas: HTMLCanvasElement, config: ChartConfig): Chart {
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("chart: 2d context unavailable");
  const g = ctx; // narrowed, non-null for the closure's lifetime

  // Working config: keep raw fields (yMin/yMax may be undefined => defaulted at draw).
  const cfg: ChartConfig = {
    series: config.series,
    xLabels: config.xLabels,
    yUnit: config.yUnit,
    yMin: config.yMin,
    yMax: config.yMax,
    marks: config.marks,
    majorTick: config.majorTick,
    tooltipTitle: config.tooltipTitle,
    tooltipExtra: config.tooltipExtra,
    gutterLeft: config.gutterLeft,
    xAxis: config.xAxis,
    legend: config.legend,
    minDpr: config.minDpr,
  };

  // Whether the pointer is over THIS canvas — it gates only which sample the
  // READOUT describes. The crosshair follows the shared cursor whether or not
  // this chart is the one being pointed at, which is what makes the stack read
  // as one playhead.
  let pointerInside = false;
  let rafPending = false;

  /**
   * Which sample this chart is currently describing.
   *
   * Two different questions used to be answered by one value. Hovering a track
   * moved the SHARED playhead, so "what is this number here" and "move the whole
   * console to this moment" were the same gesture: every mouse cross of the stack
   * dragged the map field and six other canvases along with it, and there was no
   * way to park the playhead and then point at anything — it followed the hand
   * that was pointing.
   *
   * Now they are separate. While the pointer is over THIS chart it reads its own
   * hovered sample and nothing else in the console moves; otherwise it reads the
   * shared playhead, so it still follows a scrub from the ruler or the transport.
   */
  function hoveredIndex(): number | null {
    if (pointerInside && localIdx !== null) return localIdx;
    return indexAtTime(cfg.xLabels, getCursor());
  }

  /** The sample under this chart's own pointer; null when it is elsewhere. */
  let localIdx: number | null = null;
  // Cache the last plot area so hover hit-testing matches the drawn geometry.
  let lastArea = { left: 0, right: 0, top: 0, bottom: 0 };

  const FONT_TICK = "11px system-ui, -apple-system, sans-serif";
  const FONT_LEGEND = "11px system-ui, -apple-system, sans-serif";

  function unit(): string {
    return cfg.yUnit != null && cfg.yUnit.length > 0 ? ` ${cfg.yUnit}` : "";
  }

  function wantLine(s: Series): boolean {
    // Default: draw a connecting line unless the series is points-only.
    return s.line ?? s.points !== true;
  }

  // Compute the Y domain [yMin, yMax] and the tick set.
  function computeYScale(targetTicks = 5): { yMin: number; yMax: number; ticks: YTick[] } {
    const yMin = cfg.yMin ?? 0;

    let dataMax = -Infinity;
    for (const s of cfg.series) {
      for (const v of s.data) {
        if (v != null && Number.isFinite(v) && v > dataMax) dataMax = v;
      }
    }

    let yMax: number;
    if (cfg.yMax != null) {
      yMax = cfg.yMax;
    } else if (dataMax === -Infinity) {
      yMax = yMin + 1; // no data at all
    } else {
      // Nice ceiling of the span above yMin, offset back onto yMin.
      yMax = yMin + niceCeil(dataMax - yMin > 0 ? dataMax - yMin : dataMax);
    }
    if (!(yMax > yMin)) yMax = yMin + 1; // guarantee a non-zero range

    const step = niceStep((yMax - yMin) / targetTicks);
    const dec = decimalsFor(step);
    const ticks: YTick[] = [];
    // Start at the first step multiple >= yMin, walk up to yMax.
    const start = Math.ceil((yMin - 1e-9) / step) * step;
    for (let v = start; v <= yMax + 1e-9; v += step) {
      const value = Math.abs(v) < step / 1e6 ? 0 : v;
      ticks.push({ value, label: `${value.toFixed(dec)}${unit()}` });
    }
    return { yMin, yMax, ticks };
  }

  interface Sized {
    cssW: number;
    cssH: number;
    dpr: number;
  }

  function measure(): Sized {
    const rect = canvas.getBoundingClientRect();
    const cssW = rect.width > 0 ? rect.width : canvas.clientWidth;
    const cssH = rect.height > 0 ? rect.height : canvas.clientHeight;
    const dpr = Math.max(
      cfg.minDpr ?? 0,
      window.devicePixelRatio > 0 ? window.devicePixelRatio : 1,
    );
    return { cssW, cssH, dpr };
  }

  function labeledSeries(): Series[] {
    return cfg.series.filter((s) => s.noLegend !== true);
  }

  // --- Draw passes ---------------------------------------------------------

  function drawGrid(c: ChartCtx, pal: Palette, ticks: YTick[]): void {
    g.save();
    g.lineWidth = 1;
    g.strokeStyle = pal.grid;
    // Horizontal gridlines.
    for (const t of ticks) {
      const y = Math.round(c.yPx(t.value)) + 0.5;
      g.beginPath();
      g.moveTo(c.area.left, y);
      g.lineTo(c.area.right, y);
      g.stroke();
    }
    // Faint vertical gridlines at major x ticks.
    const major = cfg.majorTick;
    for (let i = 0; i < c.xLabels.length; i++) {
      const s = major ? major(c.xLabels[i], i) : defaultMajorTick(c.xLabels[i]);
      if (s == null) continue;
      const x = Math.round(c.xPx(i)) + 0.5;
      g.beginPath();
      g.moveTo(x, c.area.top);
      g.lineTo(x, c.area.bottom);
      g.stroke();
    }
    g.restore();
  }

  function drawAxes(c: ChartCtx, pal: Palette, ticks: YTick[], showX = true): void {
    g.save();
    g.font = FONT_TICK;
    // Y tick labels: right-aligned in the left margin.
    g.fillStyle = pal.muted;
    g.textAlign = "right";
    g.textBaseline = "middle";
    for (const t of ticks) {
      g.fillText(t.label, c.area.left - 6, c.yPx(t.value));
    }
    // X major tick labels along the bottom.
    if (!showX) {
      g.restore();
      return;
    }
    g.fillStyle = pal.ink2;
    g.textAlign = "center";
    g.textBaseline = "top";
    const major = cfg.majorTick;
    for (let i = 0; i < c.xLabels.length; i++) {
      const label = major ? major(c.xLabels[i], i) : defaultMajorTick(c.xLabels[i]);
      if (label == null) continue;
      g.fillText(label, c.xPx(i), c.area.bottom + 6);
    }
    g.restore();
  }

  // Collect [x,y] pixel points for a series' finite values (spanGaps => all finite).
  function finitePoints(c: ChartCtx, s: Series): Array<[number, number]> {
    const out: Array<[number, number]> = [];
    for (let i = 0; i < s.data.length; i++) {
      const v = s.data[i];
      if (v != null && Number.isFinite(v)) out.push([c.xPx(i), c.yPx(v)]);
    }
    return out;
  }

  function drawFills(c: ChartCtx, ordered: Series[]): void {
    for (const s of ordered) {
      if (!s.fill) continue;
      g.save();
      g.fillStyle = s.fillColor ?? s.color;
      if (s.fill === "origin") {
        const pts = finitePoints(c, s);
        if (pts.length >= 2) {
          const y0 = c.yPx(0);
          const baseY = clamp(y0, c.area.top, c.area.bottom);
          g.beginPath();
          g.moveTo(pts[0][0], pts[0][1]);
          for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
          g.lineTo(pts[pts.length - 1][0], baseY);
          g.lineTo(pts[0][0], baseY);
          g.closePath();
          g.fill();
        }
      } else if (s.fill === "-1") {
        // Band between this series and the PREVIOUS series in the original array.
        const idx = cfg.series.indexOf(s);
        const prev = idx > 0 ? cfg.series[idx - 1] : null;
        if (prev) {
          // Use indices where both series are finite so edges pair up cleanly.
          const cur: Array<[number, number]> = [];
          const bot: Array<[number, number]> = [];
          for (let i = 0; i < s.data.length; i++) {
            const a = s.data[i];
            const b = prev.data[i];
            if (a != null && Number.isFinite(a) && b != null && Number.isFinite(b)) {
              cur.push([c.xPx(i), c.yPx(a)]);
              bot.push([c.xPx(i), c.yPx(b)]);
            }
          }
          if (cur.length >= 2) {
            g.beginPath();
            g.moveTo(cur[0][0], cur[0][1]);
            for (let i = 1; i < cur.length; i++) g.lineTo(cur[i][0], cur[i][1]);
            for (let i = bot.length - 1; i >= 0; i--) g.lineTo(bot[i][0], bot[i][1]);
            g.closePath();
            g.fill();
          }
        }
      }
      g.restore();
    }
  }

  function drawLines(c: ChartCtx, ordered: Series[]): void {
    for (const s of ordered) {
      if (!wantLine(s)) continue;
      const pts = finitePoints(c, s);
      if (pts.length < 2) continue;
      g.save();
      g.strokeStyle = s.color;
      g.lineWidth = s.width ?? 2;
      g.lineJoin = "round";
      g.lineCap = "round";
      g.setLineDash(s.dashed === true ? [5, 4] : []);
      g.beginPath();
      g.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
      g.stroke();
      g.restore();
    }
  }

  function drawPoints(c: ChartCtx, ordered: Series[]): void {
    for (const s of ordered) {
      if (s.points !== true) continue;
      g.save();
      g.fillStyle = s.color;
      for (const [x, y] of finitePoints(c, s)) {
        g.beginPath();
        g.arc(x, y, 2.2, 0, Math.PI * 2);
        g.fill();
      }
      g.restore();
    }
  }

  function drawLegend(pal: Palette, area: ChartCtx["area"], topY: number): void {
    const items = labeledSeries();
    if (items.length <= 1) return;
    g.save();
    g.font = FONT_LEGEND;
    g.textAlign = "left";
    g.textBaseline = "middle";
    const y = topY;
    let x = area.left;
    const sw = 10;
    const gap = 6;
    const between = 16;
    for (const s of items) {
      const w = g.measureText(s.label).width;
      if (x + sw + gap + w > area.right && x > area.left) break; // single row; stop if it overflows
      g.fillStyle = s.color;
      g.fillRect(x, y - sw / 2, sw, sw);
      g.fillStyle = pal.ink2;
      g.fillText(s.label, x + sw + gap, y);
      x += sw + gap + w + between;
    }
    g.restore();
  }

  /**
   * Veil everything OUTSIDE the work window.
   *
   * The window is the question being asked — "can this boat do this job in these
   * hours" — and every track is evidence for it. Marking it here means you can
   * read wind, wave, daylight and workability against the same span without
   * tracking your eye back to the ruler.
   *
   * Deliberately an INVERSE mask rather than a tint over the window. Seven tracks
   * each with a coloured band would put seven washes over the data people are
   * trying to read; scrimming the outside adds no colour to any trace. It is a
   * veil, not a curtain: the excluded hours stay legible, because "and if we slip
   * a day, look what happens here" is a thing you say while pointing at them.
   */
  function drawWindowMask(c: ChartCtx, pal: Palette): void {
    const cur = getCursor();
    if (cur === null) return;
    const n = cfg.xLabels.length;
    if (n < 2) return;
    const times = timesOf(cfg.xLabels);
    const first = times[0];
    const last = times[n - 1];
    if (!Number.isFinite(first) || !Number.isFinite(last) || last <= first) return;

    const endT = cur + getWindowHours() * 3_600_000;
    const frac = (t: number): number => (t - first) / (last - first);
    const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
    const x0 = c.area.left + c.width * clamp01(frac(cur));
    const x1 = c.area.left + c.width * clamp01(frac(endT));

    const g2 = c.ctx;
    g2.save();
    // Barely there. At a 12 h window on a 7-day axis this covers ~93% of the
    // chart, so anything heavier dims the console instead of marking the window.
    // The bookends do the marking; this only nudges the outside back a step.
    g2.fillStyle = pal.surface;
    g2.globalAlpha = 0.1;
    if (x0 > c.area.left) g2.fillRect(c.area.left, c.area.top, x0 - c.area.left, c.height);
    if (x1 < c.area.right) g2.fillRect(x1, c.area.top, c.area.right - x1, c.height);
    g2.restore();

    // The window's edges, so the span has stated boundaries rather than just a
    // change in density that a projector might swallow.
    g2.save();
    // The two bookends ARE the marker: full strength, full plot height, so the
    // span is stated by its boundaries rather than by everything else fading.
    g2.strokeStyle = pal.cursor;
    g2.globalAlpha = 1;
    g2.lineWidth = 2;
    g2.beginPath();
    for (const x of [x0, x1]) {
      if (x <= c.area.left || x >= c.area.right) continue;
      g2.moveTo(Math.round(x) + 0.5, c.area.top);
      g2.lineTo(Math.round(x) + 0.5, c.area.bottom);
    }
    g2.stroke();
    g2.restore();
  }

  /** The crosshair and its highlight dots. The numbers that used to be drawn in
   *  a box beside them are published to the DOM readout instead — see the note
   *  at the top of chart/readout.ts for why. */
  function drawCrosshair(c: ChartCtx, pal: Palette): void {
    const idx = hoveredIndex();
    if (idx === null) return;
    const x = c.xPx(idx);

    // The shared playhead. Deliberately stronger than a gridline: in a stack this
    // line is the only thing tying the tracks together, and in --axis grey it was
    // indistinguishable from the grid it crosses.
    g.save();
    g.strokeStyle = pal.cursor;
    g.lineWidth = 1.5;
    g.setLineDash([]);
    g.beginPath();
    g.moveTo(Math.round(x) + 0.5, c.area.top);
    g.lineTo(Math.round(x) + 0.5, c.area.bottom);
    g.stroke();

    // Highlight dots on each series at the hovered index.
    for (const s of cfg.series) {
      const v = s.data[idx];
      if (v == null || !Number.isFinite(v)) continue;
      g.fillStyle = s.color;
      g.beginPath();
      g.arc(x, c.yPx(v), 3, 0, Math.PI * 2);
      g.fill();
    }
    g.restore();
  }

  // --- readout -------------------------------------------------------------

  const readoutListeners = new Set<(m: ReadoutModel) => void>();

  /**
   * The sample the READOUT describes, which is not always the one the crosshair
   * marks. The crosshair may be absent — nothing pointed at, nothing scrubbed —
   * and the readout still has something true to say: the present hour. `now` is
   * derived from this chart's own labels rather than plumbed in, so it lands on
   * the same grid and the same clock the labels are written in.
   */
  function publishReadout(): void {
    if (readoutListeners.size === 0) return;
    const n = cfg.xLabels.length;
    const hover = pointerInside ? localIdx : null;
    const cursor = indexAtTime(cfg.xLabels, getCursor());
    const now = n > 0 ? indexAtTime(cfg.xLabels, Date.now()) : null;
    const { index, live } = readoutIndex(hover, cursor, now, n);
    const titleFn = cfg.tooltipTitle;
    const label = index === null ? "" : cfg.xLabels[index] ?? "";
    const model = buildReadout({
      series: cfg.series,
      index,
      live,
      title: index !== null && titleFn ? titleFn(index) : label,
      // The series arrive in display units already (main.ts converts through
      // ui/units.ts before handing them over) and yUnit names those units, so
      // the readout prints exactly what the axis does.
      unit: cfg.yUnit ?? "",
      extras: index !== null && cfg.tooltipExtra ? cfg.tooltipExtra(index) : [],
    });
    for (const fn of [...readoutListeners]) fn(model);
  }

  function draw(): void {
    // Per draw, not per chart-lifetime: a Day->Night switch must recolour the
    // existing charts rather than requiring them to be rebuilt.
    const pal = palette();
    const sized = measure();
    if (sized.cssW <= 0 || sized.cssH <= 0) return;

    // Retina backing store.
    const bw = Math.round(sized.cssW * sized.dpr);
    const bh = Math.round(sized.cssH * sized.dpr);
    if (canvas.width !== bw) canvas.width = bw;
    if (canvas.height !== bh) canvas.height = bh;

    g.setTransform(sized.dpr, 0, 0, sized.dpr, 0, 0);
    g.clearRect(0, 0, sized.cssW, sized.cssH);

    // Vertical margins first: the tick COUNT depends on how much height is left,
    // and the left margin then depends on how wide those ticks print.
    const showLegend = cfg.legend !== false && labeledSeries().length > 1;
    const showXAxis = cfg.xAxis !== false;
    const { top: marginTop, bottom: marginBottom } = plotMargins({
      legend: showLegend,
      xAxis: showXAxis,
    });

    // Tick density follows available height. A clip-height track in a stack has
    // room for two or three labels; five would collide into an unreadable smear.
    const plotH = sized.cssH - marginTop - marginBottom;
    const targetTicks = Math.max(2, Math.min(5, Math.floor(plotH / 26)));
    const { yMin, yMax, ticks } = computeYScale(targetTicks);

    g.font = FONT_TICK;
    let yLabelW = 0;
    for (const t of ticks) {
      const w = g.measureText(t.label).width;
      if (w > yLabelW) yLabelW = w;
    }
    // A caller-supplied gutter wins over the measured one so a stack of tracks
    // shares a single plot origin (see ChartConfig.gutterLeft).
    const marginLeft = cfg.gutterLeft ?? Math.ceil(yLabelW) + 12;
    const marginRight = 10;

    const area = {
      left: marginLeft,
      right: sized.cssW - marginRight,
      top: marginTop,
      bottom: sized.cssH - marginBottom,
    };
    if (area.right <= area.left || area.bottom <= area.top) return;
    lastArea = area;

    const n = cfg.xLabels.length;
    const span = area.right - area.left;
    const denom = n > 1 ? n - 1 : 1;
    const plotSpanY = area.bottom - area.top;
    const yRange = yMax - yMin;

    const c: ChartCtx = {
      ctx: g,
      xPx: (i: number) => area.left + (span * i) / denom,
      yPx: (v: number) => area.bottom - ((v - yMin) / yRange) * plotSpanY,
      area,
      width: span,
      height: plotSpanY,
      series: cfg.series,
      xLabels: cfg.xLabels,
    };

    const marks = cfg.marks ?? [];
    // back marks -> grid -> axes -> series (fills, lines, points) -> front marks -> legend -> hover
    for (const m of marks) if ((m.layer ?? "back") === "back") m.draw(c);

    drawGrid(c, pal, ticks);
    drawAxes(c, pal, ticks, showXAxis);

    const ordered = cfg.series
      .map((s, i) => ({ s, i }))
      .sort((a, b) => (a.s.order ?? a.i) - (b.s.order ?? b.i))
      .map((o) => o.s);

    drawFills(c, ordered);
    drawLines(c, ordered);
    drawPoints(c, ordered);

    for (const m of marks) if (m.layer === "front") m.draw(c);

    drawWindowMask(c, pal);

    if (showLegend) drawLegend(pal, area, LEGEND_H / 2 + 4);

    drawCrosshair(c, pal);

    // Last: the numbers, to whoever is showing them. Publishing from the paint
    // keeps the readout and the crosshair describing the same instant by
    // construction — they cannot drift because there is only one place either
    // of them is decided.
    publishReadout();
  }

  function requestDraw(): void {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      draw();
    });
  }

  // --- Interaction ---------------------------------------------------------

  function onPointerMove(e: PointerEvent): void {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // Only the x matters now. The pointer's y used to place the tooltip box,
    // which is the whole reason the box was in the plot area at all.
    const n = cfg.xLabels.length;
    if (n === 0 || lastArea.right <= lastArea.left) return;
    const denom = n > 1 ? n - 1 : 1;
    const t = (x - lastArea.left) / (lastArea.right - lastArea.left);
    pointerInside = true;
    localIdx = clamp(Math.round(t * denom), 0, n - 1);
    // ONE canvas repaints, not eight. Reading a value off a track is inspection,
    // not navigation; scrubbing the console is the ruler's job.
    requestDraw();
  }

  function onPointerLeave(): void {
    if (!pointerInside) return;
    pointerInside = false;
    localIdx = null;
    // The playhead stays where it was left; this chart falls back to showing it.
    requestDraw();
  }

  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerleave", onPointerLeave);

  // Any move of the shared playhead repaints this chart, even when the pointer is
  // somewhere else entirely (another track, the map, the transport).
  const unsubscribeCursor = subscribeCursor(() => requestDraw());
  const unsubscribeWindow = subscribeWindow(() => requestDraw());
  // A Local/UTC flip moves the axis midnights and retitles the tooltip; both are
  // read at draw time, so a repaint is the whole response.
  const unsubscribeClock = subscribeTimeMode(() => requestDraw());

  const ro = new ResizeObserver(() => requestDraw());
  ro.observe(canvas.parentElement ?? canvas);

  // Initial paint.
  draw();

  return {
    update(partial: Partial<ChartConfig>): void {
      if (partial.series !== undefined) cfg.series = partial.series;
      if (partial.xLabels !== undefined) cfg.xLabels = partial.xLabels;
      if ("yUnit" in partial) cfg.yUnit = partial.yUnit;
      if ("yMin" in partial) cfg.yMin = partial.yMin;
      if ("yMax" in partial) cfg.yMax = partial.yMax;
      if ("marks" in partial) cfg.marks = partial.marks;
      if ("majorTick" in partial) cfg.majorTick = partial.majorTick;
      if ("tooltipTitle" in partial) cfg.tooltipTitle = partial.tooltipTitle;
      if ("tooltipExtra" in partial) cfg.tooltipExtra = partial.tooltipExtra;
      if ("gutterLeft" in partial) cfg.gutterLeft = partial.gutterLeft;
      if ("xAxis" in partial) cfg.xAxis = partial.xAxis;
      if ("legend" in partial) cfg.legend = partial.legend;
      if ("minDpr" in partial) cfg.minDpr = partial.minDpr;
      // No hover index to clamp — it is re-derived from the shared cursor each draw,
      // so a data swap can never leave a stale index behind.
      draw();
    },
    subscribeReadout(fn: (m: ReadoutModel) => void): () => void {
      readoutListeners.add(fn);
      // Paint the newcomer immediately: a readout that stays blank until the
      // first pointer move is the blank-default the table already ruled out.
      publishReadout();
      return () => {
        readoutListeners.delete(fn);
      };
    },
    destroy(): void {
      readoutListeners.clear();
      unsubscribeCursor();
      unsubscribeWindow();
      unsubscribeClock();
      ro.disconnect();
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
    },
  };
}
